import {
  Table as ChakraTable,
  CircularProgress,
  HStack,
  Td,
  Tr,
  VStack,
  MenuButton,
  Box,
  Skeleton,
} from '@chakra-ui/react';
import { Checkbox, IconButton, Tag, Tooltip, TruncatableText } from 'Atoms';
import { UserAvatar } from 'Organisms';
import { Typography, TypographyVariant } from 'Tokens';
import { ArrowNarrowRightIcon } from 'Tokens/Icons/Direction';
import { StructureNode } from 'containers/Esrs/pieces/Assessment/Structure';
import {
  GetDisclosureRequirementsOwnerQuery_,
  ShortUser,
  useGetDrDatapointsTasksQuery,
  User,
} from 'models';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Menu } from 'Molecules/Menu';
import { DREnums } from 'containers/Esrs';
import { useTranslation } from 'utils/translation';
import { SelectedRowData } from '../../Metrics/MetricsTable/MetricsTableComponents/SelectedRows/SelectedRows';
import { WarningIcon } from 'Tokens/Icons/Status';
import { DocumentIcon } from 'Tokens/Icons/Data';
import { createTaskReference, getTaskDueDate, mfStandardRefs } from 'containers/Esrs/utils';
import { EmptyOwner } from 'Atoms/EmptyOwner';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

const ActionAndTargetButton = ({
  targetsCount,
  actionsCount,
  type,
}: {
  targetsCount: number;
  actionsCount: number;
  type?: string;
}) => {
  const count = useMemo(() => (type === DREnums.action ? actionsCount : targetsCount), [type]);

  const getActionTooltipText = useMemo(() => {
    if (actionsCount === 0) return "You don't have any actions";
    return `You have ${actionsCount} ${actionsCount === 1 ? 'action' : 'actions'}`;
  }, [actionsCount]);

  const getTargetTooltipText = useMemo(() => {
    if (targetsCount === 0) return "You don't have any targets";
    return `You have ${targetsCount} ${targetsCount === 1 ? 'target' : 'targets'}`;
  }, [targetsCount]);

  const toolTipText = useMemo(
    () => (type === DREnums.action ? getActionTooltipText : getTargetTooltipText),
    [type, actionsCount, targetsCount]
  );

  return (
    <Menu
      customMenuButton={
        <Tooltip placement={'bottom-start'} label={toolTipText}>
          <MenuButton onClick={(e: any) => e.stopPropagation()} _hover={{ cursor: 'pointer' }}>
            <Tag
              variant={count ? 'info' : ''}
              backgroundColor={!count ? 'bg.pressed' : ''}
              size="xs"
              title={String(count)}
            />
          </MenuButton>
        </Tooltip>
      }
      sections={[]}
      size="sm"
    />
  );
};

export const CompanyDrProgress = ({
  drRef,
  assessmentId,
  isActionOrTarget,
  standardRef,
  parentAssessmentId,
  frameworkRef,
  size,
  textVariant,
}: {
  drRef: string;
  assessmentId: string;
  isActionOrTarget: boolean;
  standardRef: string;
  parentAssessmentId: string | undefined;
  frameworkRef: string;
  size?: string;
  textVariant?: TypographyVariant;
}) => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/disclosure-requirement-progress',
      {
        drRef,
        assessmentId,
        standardRef,
        parentAssessmentId,
        type: 'company',
        frameworkRef,
      }
    );
    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [drRef, assessmentId, standardRef, parentAssessmentId]);

  return (
    <Skeleton isLoaded={!loading}>
      <Tooltip
        placement={'bottom-start'}
        label={isActionOrTarget ? t('common:targetsAndActionsProgressBar') : ''}
      >
        <HStack spacing="8px">
          <CircularProgress
            size={size ?? '16px'}
            thickness="12px"
            color="text.info"
            value={isActionOrTarget ? 0 : Number(progress ?? 0)}
          />
          <Typography
            variant={textVariant ?? 'body'}
            color={isActionOrTarget ? 'text.hint' : 'text.primary'}
          >
            {!isActionOrTarget ? `${progress}%` : 'N/A'}
          </Typography>
        </HStack>
      </Tooltip>
    </Skeleton>
  );
};

export const GroupDrProgress = ({
  drRef,
  assessmentId,
  isActionOrTarget,
  standardRef,
  frameworkRef,
  size,
  textVariant,
}: {
  drRef: string;
  assessmentId: string;
  isActionOrTarget: boolean;
  standardRef: string;
  frameworkRef: string;
  size?: string;
  textVariant?: TypographyVariant;
}) => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/disclosure-requirement-progress',
      {
        drRef,
        assessmentId,
        standardRef,
        type: 'group',
        frameworkRef,
      }
    );
    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [drRef, assessmentId, standardRef]);
  return (
    <Skeleton isLoaded={!loading}>
      <Tooltip
        placement={'bottom-start'}
        label={isActionOrTarget ? t('common:targetsAndActionsProgressBar') : ''}
      >
        <HStack spacing="8px">
          <CircularProgress
            size={size ?? '16px'}
            thickness="12px"
            color="text.info"
            value={isActionOrTarget ? 0 : Number(progress ?? 0)}
          />
          <Typography
            variant={textVariant ?? 'body'}
            color={isActionOrTarget ? 'text.hint' : 'text.primary'}
          >
            {!isActionOrTarget ? `${progress}%` : 'N/A'}
          </Typography>
        </HStack>
      </Tooltip>
    </Skeleton>
  );
};

export const BusinessUnitDrProgress = ({
  drRef,
  assessmentId,
  isActionOrTarget,
  standardRef,
  parentAssessmentId,
  reportingUnitId,
  frameworkRef,
  size,
  textVariant,
}: {
  drRef: string;
  assessmentId: string;
  isActionOrTarget: boolean;
  standardRef: string;
  parentAssessmentId: string | undefined;
  reportingUnitId: string;
  frameworkRef: string;
  size?: string;
  textVariant?: TypographyVariant;
}) => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');
  const nhost = useNhostClient();

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.post<{ progress: string }>(
      '/esrs/progress/disclosure-requirement-progress',
      {
        drRef,
        assessmentId,
        standardRef,
        parentAssessmentId,
        reportingUnitId,
        type: 'reportingUnit',
        frameworkRef,
      }
    );
    if (res.body.progress) {
      setProgress(res.body.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [drRef, assessmentId, standardRef, parentAssessmentId]);
  return (
    <Skeleton isLoaded={!loading}>
      <Tooltip
        placement={'bottom-start'}
        label={isActionOrTarget ? t('common:targetsAndActionsProgressBar') : ''}
      >
        <HStack spacing="8px">
          <CircularProgress
            size={size ?? '16px'}
            thickness="12px"
            color="text.info"
            value={isActionOrTarget ? 0 : Number(progress ?? 0)}
          />
          <Typography
            variant={textVariant ?? 'body'}
            color={isActionOrTarget ? 'text.hint' : 'text.primary'}
          >
            {!isActionOrTarget ? `${progress}%` : 'N/A'}
          </Typography>
        </HStack>
      </Tooltip>
    </Skeleton>
  );
};

const DrDueDate = ({
  datapointsTaskData,
  drRef,
  esrsAssessmentId,
  reportingUnitId,
  dpDataLoading,
}: {
  datapointsTaskData: SelectedRowData[];
  drRef: string;
  esrsAssessmentId: string;
  reportingUnitId: string;
  dpDataLoading: boolean;
}) => {
  const taskData = datapointsTaskData.filter((data) => data.disclosureRequirementRef === drRef);
  const taskReferences = taskData?.map((data) =>
    createTaskReference({
      metricRef: data.metricRef,
      assessmentId: esrsAssessmentId,
      reportingUnitId: reportingUnitId,
      tagCombination: data.tagCombination ?? '',
      timeframe: data.timeframe,
    })
  );
  const { data: tasks, loading } = useGetDrDatapointsTasksQuery({
    variables: { taskReferences: taskReferences },
    skip: !taskReferences?.length,
  });
  const latestDueDate = getTaskDueDate(tasks?.Task ?? []);
  return (
    <Skeleton isLoaded={!(dpDataLoading || loading)}>
      <Typography
        variant="body"
        whiteSpace="nowrap"
        color={
          latestDueDate.state === 'overdue'
            ? 'text.critical'
            : latestDueDate.state === 'completed'
              ? 'text.hint'
              : 'text.default'
        }
      >
        {latestDueDate.date}
      </Typography>
    </Skeleton>
  );
};

export const DisclosureRequirementTable = ({
  title,
  requirements,
  companyLevelReportingUnitId,
  owner,
  isGroup,
  parentAssessmentId,
  isConfig,
  selectedDrs,
  datapointsTaskData,
  setSelectedDrs,
  drOwnerData,
  drRefsMissingTagsConfiguration,
  dpDataLoading,
  drOwnerLoading,
}: {
  title: string;
  requirements: StructureNode[];
  owner?: Partial<User> | null;
  companyLevelReportingUnitId: string;
  isGroup: boolean;
  parentAssessmentId?: string | undefined;
  isConfig?: boolean;
  drOwnerData?: GetDisclosureRequirementsOwnerQuery_['DisclosureRequirementOwner'];
  datapointsTaskData: SelectedRowData[];
  selectedDrs: string[];
  setSelectedDrs: React.Dispatch<React.SetStateAction<string[]>>;
  drRefsMissingTagsConfiguration?: string[];
  dpDataLoading: boolean;
  drOwnerLoading: boolean;
}) => {
  const { esrsAssessmentId, reportingUnitId = '', standardRef = '', framework = '' } = useParams();
  const [hoveredRowIndex, setHoveredRowIndex] = useState(-1);
  const navigate = useNavigate();
  const isBusinessUnit = useMemo(() => !!reportingUnitId, [reportingUnitId]);
  const rowStyling = {
    p: '8px',
    borderColor: 'border.decorative',
  };

  const handleRowClick = (drRef: string, category?: string) => {
    if (isBusinessUnit || isConfig) navigate(`disclosure-requirement/${drRef}`);
    else navigate(`disclosure-requirement/${category}/${drRef}`);
  };

  const handleSelectDr = (drRef: string) => {
    if (selectedDrs.includes(drRef)) setSelectedDrs(selectedDrs.filter((std) => std !== drRef));
    else setSelectedDrs((prev) => [...prev, drRef]);
  };
  const isMFStandard = mfStandardRefs.includes(standardRef);

  return (
    <VStack alignItems="start" gap="4px">
      <Typography variant="overline" color="text.hint">
        {title.toUpperCase()}
      </Typography>
      <ChakraTable borderTop="1px solid" borderColor="border.decorative">
        {requirements.map((req, index) => {
          const drOwner = drOwnerData?.find(
            (dr) =>
              dr.disclosureRequirementRef === req.reference ||
              dr.mfDisclosureRequirementRef === req.reference
          )?.owner;
          return (
            <Tr
              _hover={{ background: 'bg.hover', cursor: 'pointer' }}
              transition="0.16s all"
              onMouseEnter={() => setHoveredRowIndex(index)}
              onMouseLeave={() => setHoveredRowIndex(-1)}
            >
              {/* icon */}
              {isConfig ? (
                <Td
                  w="28px"
                  borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                  onClick={() => handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')}
                  {...rowStyling}
                >
                  {drRefsMissingTagsConfiguration?.includes(req.reference as string) ? (
                    <WarningIcon boxSize="16px" color="text.warning" />
                  ) : (
                    <DocumentIcon boxSize="16px" />
                  )}
                </Td>
              ) : (
                <Td
                  w="28px"
                  borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                  {...rowStyling}
                >
                  <Checkbox
                    isChecked={selectedDrs?.includes(req.reference ?? '')}
                    pt="3px"
                    verticalAlign="none"
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectDr(req.reference ?? '');
                    }}
                  />
                </Td>
              )}
              {/* ref */}
              <Td
                w="92px"
                borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                onClick={() => handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')}
                {...rowStyling}
              >
                <TruncatableText variant="body" text={isMFStandard ? '' : (req.reference ?? '')} />
              </Td>
              {/* title */}
              <Td
                w="540px"
                borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                onClick={() => handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')}
                {...rowStyling}
              >
                <Box display="flex" gap="8px">
                  <TruncatableText
                    w="fit-content"
                    tooltipMaxWidth="400px"
                    variant="bodyStrong"
                    text={req.title}
                  />
                  {(req.type === DREnums.action || req.type === DREnums.target) && (
                    <ActionAndTargetButton
                      targetsCount={req.targetsCount || 0}
                      actionsCount={req.actionsCount || 0}
                      type={req.type}
                    />
                  )}
                </Box>
              </Td>
              {isConfig ? (
                <Td
                  borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                  onClick={() => handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')}
                  {...rowStyling}
                >
                  <Tag
                    size="xs"
                    title={req.isConfigured ? 'Done' : 'To setup'}
                    variant={req.isConfigured ? 'success' : 'warning'}
                    borderRadius="6px"
                  />
                </Td>
              ) : (
                <>
                  <Td
                    w="88px"
                    borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                    onClick={() =>
                      handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')
                    }
                    {...rowStyling}
                  >
                    {isBusinessUnit ? (
                      <BusinessUnitDrProgress
                        drRef={req.reference ?? ''}
                        assessmentId={esrsAssessmentId ?? ''}
                        isActionOrTarget={
                          req.type === DREnums.action || req.type === DREnums.target
                        }
                        standardRef={standardRef}
                        parentAssessmentId={parentAssessmentId}
                        reportingUnitId={reportingUnitId}
                        frameworkRef={framework}
                      />
                    ) : isGroup ? (
                      <GroupDrProgress
                        drRef={req.reference ?? ''}
                        assessmentId={esrsAssessmentId ?? ''}
                        isActionOrTarget={
                          req.type === DREnums.action || req.type === DREnums.target
                        }
                        standardRef={standardRef}
                        frameworkRef={framework}
                      />
                    ) : (
                      <CompanyDrProgress
                        drRef={req.reference ?? ''}
                        assessmentId={esrsAssessmentId ?? ''}
                        isActionOrTarget={
                          req.type === DREnums.action || req.type === DREnums.target
                        }
                        standardRef={standardRef}
                        parentAssessmentId={parentAssessmentId}
                        frameworkRef={framework}
                      />
                    )}
                  </Td>
                  <Td
                    w="96px"
                    borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                    onClick={() =>
                      handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')
                    }
                    {...rowStyling}
                  >
                    <DrDueDate
                      datapointsTaskData={datapointsTaskData}
                      drRef={req.reference ?? ''}
                      esrsAssessmentId={esrsAssessmentId ?? ''}
                      reportingUnitId={
                        isBusinessUnit ? reportingUnitId : companyLevelReportingUnitId
                      }
                      dpDataLoading={dpDataLoading}
                    />
                  </Td>
                  <Td
                    w="64px"
                    borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                    onClick={() =>
                      handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')
                    }
                    {...rowStyling}
                  >
                    <Skeleton isLoaded={!drOwnerLoading}>
                      {drOwner ? (
                        <UserAvatar
                          user={
                            drOwnerData?.find(
                              (dr) =>
                                dr.disclosureRequirementRef === req.reference ||
                                dr.mfDisclosureRequirementRef === req.reference
                            )?.owner ?? (owner as ShortUser)
                          }
                          size="xs"
                          border="none"
                        />
                      ) : (
                        <EmptyOwner size="20px" />
                      )}
                    </Skeleton>
                  </Td>
                </>
              )}

              {/* action */}
              <Td
                w="54px"
                borderBottom={index === requirements.length - 1 ? 'none' : '1px solid'}
                onClick={() => handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')}
                {...rowStyling}
              >
                <IconButton
                  aria-label="side-bar"
                  size="sm"
                  variant="ghost"
                  bg={hoveredRowIndex === index ? 'bg.hover' : ''}
                  transition="0.16s all"
                  icon={<ArrowNarrowRightIcon />}
                  onClick={() => handleRowClick(req.reference ?? '', req.key?.split('_')[0] ?? '')}
                />
              </Td>
            </Tr>
          );
        })}
      </ChakraTable>
    </VStack>
  );
};
